/**
 * 分镜音效 API Hook
 * 对接 /api/v1/storyboard-resources 分镜音效接口
 */
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import {
  storyboardResourcesApi,
  type CreateStoryboardSoundEffectPayload,
  type StoryboardSoundEffectResponse,
  type UpdateStoryboardSoundEffectPayload,
} from '@/services/api/storyboard-resources';
import { invalidateStoryboardBoardQueries } from '@/hooks/api/useStoryboardBoard';

const EMPTY_SOUND_EFFECTS: StoryboardSoundEffectResponse[] = [];

export const storyboardSoundEffectsKeys = {
  all: ['storyboard-sound-effects'] as const,
  list: (storyboardId: string) => [...storyboardSoundEffectsKeys.all, storyboardId] as const,
};

export function useStoryboardSoundEffects(
  storyboardId: string | null,
  options?: { enabled?: boolean; projectId?: string | null }
) {
  const queryClient = useQueryClient();
  const queryKey = storyboardId ? storyboardSoundEffectsKeys.list(storyboardId) : null;
  const enabled = options?.enabled ?? true;
  const projectId = options?.projectId ?? null;

  const query = useQuery({
    queryKey: storyboardSoundEffectsKeys.list(storyboardId!),
    queryFn: () => storyboardResourcesApi.getStoryboardSoundEffects(storyboardId!),
    enabled: !!storyboardId && enabled,
    staleTime: 1000 * 30,
  });

  const createMutation = useMutation({
    mutationFn: (payload: CreateStoryboardSoundEffectPayload) =>
      storyboardResourcesApi.createStoryboardSoundEffect(storyboardId!, payload),
    onSuccess: async () => {
      if (!storyboardId) return;
      await invalidateSoundEffectRefreshQueries(queryClient, { storyboardId, projectId });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (params: { soundEffectId: string; payload: UpdateStoryboardSoundEffectPayload }) =>
      storyboardResourcesApi.updateStoryboardSoundEffect(params.soundEffectId, params.payload),
    onMutate: async (params) => {
      if (!queryKey) return { previous: undefined as StoryboardSoundEffectResponse[] | undefined };
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<StoryboardSoundEffectResponse[]>(queryKey);
      queryClient.setQueryData<StoryboardSoundEffectResponse[]>(queryKey, (current = []) =>
        current.map((item) =>
          item.soundEffectId === params.soundEffectId ? { ...item, ...params.payload } : item
        )
      );
      return { previous };
    },
    onError: (_error, _params, context) => {
      if (!queryKey) return;
      queryClient.setQueryData(queryKey, context?.previous);
    },
    onSettled: () => {
      if (!queryKey) return;
      void invalidateSoundEffectRefreshQueries(queryClient, {
        storyboardId: queryKey[1],
        projectId,
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (soundEffectId: string) =>
      storyboardResourcesApi.deleteStoryboardSoundEffect(soundEffectId),
    onMutate: async (soundEffectId) => {
      if (!queryKey) return { previous: undefined as StoryboardSoundEffectResponse[] | undefined };
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<StoryboardSoundEffectResponse[]>(queryKey);
      queryClient.setQueryData<StoryboardSoundEffectResponse[]>(queryKey, (current = []) =>
        current.filter((item) => item.soundEffectId !== soundEffectId)
      );
      return { previous };
    },
    onError: (_error, _soundEffectId, context) => {
      if (!queryKey) return;
      queryClient.setQueryData(queryKey, context?.previous);
    },
    onSettled: () => {
      if (!queryKey) return;
      void invalidateSoundEffectRefreshQueries(queryClient, {
        storyboardId: queryKey[1],
        projectId,
      });
    },
  });

  return {
    soundEffects: query.data ?? EMPTY_SOUND_EFFECTS,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isStale: query.isStale,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    createSoundEffect: createMutation.mutateAsync,
    updateSoundEffect: updateMutation.mutateAsync,
    deleteSoundEffect: deleteMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}

/**
 * 失效音效相关缓存（音效列表 + 分镜 board）。
 */
export async function invalidateSoundEffectRefreshQueries(
  queryClient: Pick<QueryClient, 'invalidateQueries'>,
  input: {
    storyboardId: string;
    projectId?: string | null;
  }
) {
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: storyboardSoundEffectsKeys.list(input.storyboardId),
    }),
    invalidateStoryboardBoardQueries(queryClient, input.projectId),
  ]);
}

/**
 * 返回一个函数，调用后失效音效相关缓存（音效列表 + 分镜 board）。
 */
export function useInvalidateSoundEffectRefreshQueries() {
  const queryClient = useQueryClient();
  return useCallback(
    (input: { storyboardId: string; projectId?: string | null }) =>
      invalidateSoundEffectRefreshQueries(queryClient, input),
    [queryClient]
  );
}
